import { MutationCache, QueryClient } from '@tanstack/react-query';

/**
 * App-wide React Query client.
 *
 * GC TIME IS THE ONE THAT DECIDES WHETHER NAVIGATION FEELS INSTANT.
 * `gcTime` is how long a query's data survives after its LAST OBSERVER
 * unmounts — which, in a router that swaps the whole view on every route
 * change, is "how long you can be on another screen before coming back costs a
 * full refetch and a skeleton". The v5 default is 5 minutes, and leaving it
 * there is exactly the reported symptom: go to Timeline, spend six minutes
 * there, come back to Resilience, and it is cold again.
 *
 * 45 minutes instead. There are a handful of these queries, each a few hundred
 * KB, and the alternative to holding them is re-running a compose that costs
 * seconds server-side. `staleTime` stays at 30s, so the data still refreshes in
 * the background on return — the user just does not watch a skeleton while it
 * happens.
 *
 * IT CANNOT SHOW YOU 45-MINUTE-OLD DATA. `gcTime` is retention, not freshness —
 * the two are independent, and only `staleTime` decides whether to refetch.
 * Coming back to a screen after any gap: the cached payload paints immediately
 * (that is the win), and because it is older than `staleTime` React Query
 * refetches it in the background on mount — `refetchOnMount` is left at its
 * default of `true`. So the worst you can look at is one refetch round trip
 * behind, not 45 minutes. A mutation additionally invalidates `['view']`
 * outright, so your own edits never wait.
 *
 * The staleness window that DOES exist is older than this setting and unchanged
 * by it: `refetchOnWindowFocus: false` means a tab left open on one screen will
 * sit on whatever it last fetched until something re-mounts it. A reload clears
 * it. That was affordable when a view cost seconds to serve; now that a cache
 * hit is cheap it may be worth turning focus-refetch back on, but that is a
 * separate change with its own request-volume cost.
 *
 * NO CROSS-RELOAD PERSISTENCE, STILL — but the original reasoning for that is
 * now half wrong and worth restating honestly. It was: the expensive part is
 * absorbed server-side (the KV view cache) and the view endpoints send
 * `Cache-Control`, so a refresh revalidates via cheap 304s rather than
 * rehydrating stale localStorage bytes. Measured 2026-09-20: the server cache
 * cost ~2.9s to CONSULT before anything was served (fixed — see
 * `docs/features/view-caching-audit.md` §1), and over 7 days prod served
 * ZERO 304s on `/views/*`. Persistence may well be worth revisiting; it should
 * be revisited against the post-fix numbers, not on the old premise.
 *
 * Any successful mutation invalidates the compound matrix views
 * (`['view', …]`) so the UI re-reads after a write. The server cache keys on a
 * data fingerprint, so that refetch returns freshly recomputed data.
 * (Background completions — onboarding / backfill — invalidate the same key
 * from their polling hooks; see `useViews.ts`.)
 */

/**
 * How long an unobserved query's data is kept. Deliberately far longer than
 * `staleTime`: staleness decides whether to REFETCH, gcTime decides whether
 * there is anything to show while that happens.
 */
export const VIEW_GC_TIME_MS = 45 * 60_000;

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['view'] });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: VIEW_GC_TIME_MS,
      // Don't refetch on window focus by default — the app doesn't need
      // it and the polling pages set their own refetchInterval.
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});
